
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, Loader2 } from 'lucide-react';
import { Quest } from '@/types/quests';
import QuestsList from './QuestsList';
import QuestDialog from './QuestDialog';
import { useToast } from '@/hooks/use-toast';

const QuestsManagement: React.FC = () => {
  const { t } = useTranslation(['admin', 'quests', 'common']);
  const { toast } = useToast();
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentQuest, setCurrentQuest] = useState<Quest | null>(null);

  const fetchQuests = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('quests')
        .select('*, badges(*)')
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      setQuests(data || []);
    } catch (error) {
      console.error('Error fetching quests:', error);
      toast({
        title: t('common:error'),
        description: t('admin:errorFetchingData'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchQuests();
    setRefreshing(false);
  };

  const handleAddQuest = () => {
    setCurrentQuest(null);
    setIsDialogOpen(true);
  };

  const handleEditQuest = (quest: Quest) => {
    setCurrentQuest(quest);
    setIsDialogOpen(true);
  };

  const handleQuestSaved = async () => {
    setIsDialogOpen(false);
    await fetchQuests();
    toast({
      title: t('admin:success'),
      description: t('admin:questSavedSuccess'),
    });
  };

  const handleDeleteQuest = async (questId: string) => {
    try {
      const { error } = await supabase
        .from('quests')
        .delete()
        .eq('id', questId);
        
      if (error) {
        throw error;
      }
      
      setQuests(quests.filter(quest => quest.id !== questId));
      toast({
        title: t('admin:success'),
        description: t('admin:questDeletedSuccess'),
      });
    } catch (error) {
      console.error('Error deleting quest:', error);
      toast({
        title: t('common:error'),
        description: t('admin:errorDeletingQuest'),
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchQuests();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">{t('admin:manageQuests')}</h2>
          <p className="text-muted-foreground">{t('admin:manageChallengesDescription')}</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="hidden sm:inline">{t('common:refresh')}</span>
          </Button>
          
          <Button 
            onClick={handleAddQuest}
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            <span>{t('admin:newQuest')}</span>
          </Button>
        </div>
      </div>
      
      <QuestsList 
        quests={quests} 
        loading={loading} 
        onEdit={handleEditQuest}
        onDelete={handleDeleteQuest}
      />
      
      <QuestDialog 
        isOpen={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)}
        onSave={handleQuestSaved}
        quest={currentQuest}
      />
    </div>
  );
};

export default QuestsManagement;
